import { ListSpacesParams } from '~/shared/space/space-list.params'
import Geohash from 'latlon-geohash'
import { isVenueTrustSignalDomain, VENUE_TRUST_SIGNAL_LABELS, type VenueTrustSignalDomain } from '~/shared/venue/trust-signals'

export const DEFAULT_LOCATION_RADIUS_MILES = 1

export const LABEL_TO_SPACE_TYPE: Record<string, string> = {
  'Private Dining Room': 'private_dining_room',
  'Venue Buyout': 'venue_buyout',
  'Main Dining Room': 'main_dining_room',
  'Bar/Lounge': 'bar_lounge',
  'Rooftop/Terrace': 'rooftop_terrace',
  Ballroom: 'ballroom',
  'Boat/Yacht': 'boat_yacht',
  'Prefunction Space': 'prefunction_space',
  Loft: 'loft',
  'Night Club': 'night_club',
  'Raw Space': 'raw_space',
  Balcony: 'balcony',
  Nook: 'nook',
  Mezzanine: 'mezzanine',
  Section: 'section',
  Atrium: 'atrium',
  Garden: 'garden',
  Restaurant: 'restaurant',
  Auditorium: 'auditorium',
  Boardroom: 'boardroom',
}
export const LABEL_TO_PRIVACY: Record<string, string> = {
  Buyout: 'buyout',
  Private: 'private',
  'Practically Private': 'practically_private',
  'Semi-Private': 'semi_private',
  'Main Dining Area': 'main_dining_area',
}
export const LABEL_TO_NOISE: Record<string, string> = {
  'Pin Drop': 'pin_drop',
  Quiet: 'quiet',
  Moderate: 'moderate',
  Boisterous: 'boisterous',
  'N/A (Buyout)': 'na',
}
export const LABEL_TO_AMENITY: Record<string, string> = {
  'Private entrance': 'private_entrance',
  'Private entrace': 'private_entrance',
  'Outdoor space / Rooftop': 'outdoor_space_rooftop',
  'Private bar': 'private_bar',
  'ADA Compliant': 'ada_compliant',
  'Street Level Access': 'street_level_access',
  Stage: 'stage',
  'Dance Floor': 'dance_floor',
  'Private restroom': 'private_restroom',
}
export const LABEL_TO_AV: Record<string, string> = {
  'Projector / TV': 'projector_tv',
  'Wi-Fi': 'wifi',
  'In-house sound system': 'inhouse_sound_system',
  'Audio Line-In': 'audio_line_in',
  Microphone: 'microphone',
}
export const LABEL_TO_OUTDOOR: Record<string, string> = {
  'Full Outdoor, No Cover': 'full_outdoor_no_cover',
  'Full Outdoor, Cover': 'full_outdoor_cover',
  'Indoor / Outdoor': 'indoor_outdoor',
  'No Outdoor': 'no_outdoor',
}
export const LABEL_TO_WINDOWS: Record<string, string> = {
  'Outdoor-facing': 'outdoor',
  'Indoor-facing': 'indoor',
  Skylight: 'skylight',
  'N/A': 'na',
  None: 'none',
}
export const LABEL_TO_MICHELIN: Record<string, string> = {
  '3 Stars': '3',
  '2 Stars': '2',
  '1 Star': '1',
  'Bib Gourmand': 'bib',
}

const PRICE_POINT_FILTER_LEVELS = [1, 2, 3, 4] as const
const GOOGLE_RATING_FILTER_THRESHOLDS = ['4.5', '4.2', '4.0', '3.5'] as const

const createFalseStringRecord = (keys: readonly string[]): Record<string, boolean> =>
  keys.reduce<Record<string, boolean>>((record, key) => {
    record[key] = false
    return record
  }, {})

const createFalseNumberRecord = (keys: readonly number[]): Record<number, boolean> =>
  keys.reduce<Record<number, boolean>>((record, key) => {
    record[key] = false
    return record
  }, {})

// LABEL_TO_VENUE_HIGHLIGHT
export const VENUE_HIGHLIGHT_LABEL_TO_SLUG: Record<string, string> = {
  'Notable Chef': 'notable_chef',
  'Hard to book': 'hard_to_book',
  'Well Designed': 'well_designed',
  Trendy: 'trendy',
}

// SLUG_TO_VENUE_HIGHLIGHT
export const VENUE_HIGHLIGHT_SLUG_TO_LABEL: Record<string, string> = {
  notable_chef: 'Notable Chef',
  hard_to_book: 'Hard to book',
  well_designed: 'Well Designed',
  trendy: 'Trendy',
}

export type FiltersState = {
  location: string
  locationPlaceLabel?: string
  vibe: string
  locationLat?: number
  locationLng?: number
  // When set, overrides location/radius with explicit map bounds
  boundsNwGeohash?: string
  boundsSeGeohash?: string
  standingGuestCount?: number
  seatedGuestCount?: number
  fbMin?: number
  fbMax?: number
  venueName: string
  googlePlaceId?: string
  radiusMiles?: number
  spaceTypes: Record<string, boolean>
  privacyTypes: Record<string, boolean>
  noisePrivacy: Record<string, boolean>
  amenities: Record<string, boolean>
  av: Record<string, boolean>
  outdoor: Record<string, boolean>
  windows: Record<string, boolean>
  pricePoints: Record<number, boolean>
  google: Record<string, boolean>
  michelin: Record<string, boolean>
  venueTypes: Record<string, boolean>
  venueHighlights: Record<string, boolean>
  editorialRecognition?: Partial<Record<VenueTrustSignalDomain, boolean>>
  topHospitalityGroups?: boolean
  cuisines: Record<string, boolean>
  neighborhoods: Record<string, boolean>
  // Dynamic label -> id maps (for mapping selections to params)
  labelToCuisineId?: Record<string, string | number>
  labelToVenueTypeId?: Record<string, string | number>
  labelToNeighborhoodId?: Record<string, string | number>
  // Per-entity venue tag selections. Key = tag id; value = checked.
  venueTags?: Record<number, boolean>
  // Tag id -> venue ids that carry that tag. Populated from the venue-tag API
  // and used by filtersToParams to translate selected tags into a venue_ids
  // filter for Typesense.
  venueTagToVenueIds?: Record<number, number[]>
  venueTagLabelById?: Record<number, string>
}

export function createEmptyFiltersState(): FiltersState {
  return {
    location: '',
    vibe: '',
    venueName: '',
    spaceTypes: {},
    privacyTypes: {},
    noisePrivacy: {},
    amenities: {},
    av: {},
    outdoor: {},
    windows: {},
    pricePoints: createFalseNumberRecord(PRICE_POINT_FILTER_LEVELS),
    google: createFalseStringRecord(GOOGLE_RATING_FILTER_THRESHOLDS),
    michelin: createFalseStringRecord(Object.keys(LABEL_TO_MICHELIN)),
    venueTypes: {},
    venueHighlights: {},
    editorialRecognition: {},
    topHospitalityGroups: false,
    cuisines: {},
    neighborhoods: {},
  }
}

const PRICE_POINT_SLUG_BY_LEVEL: Record<number, string> = {
  1: 'budget',
  2: 'low',
  3: 'medium',
  4: 'high',
}

export function selectedMapToKeys(selectedMap: Record<string, boolean> | undefined | null, labelToKey: Record<string, string>, options?: { special?: (label: string) => string | null }): string[] {
  if (!selectedMap) return []
  const items: string[] = []
  for (const [label, isChecked] of Object.entries(selectedMap)) {
    if (!isChecked) continue
    const special = options?.special?.(label)
    if (special) {
      items.push(special)
      continue
    }
    const key = labelToKey[label]
    if (key) items.push(key)
  }
  return items
}

const selectedMappedFilterValues = (
  selectedMap: Record<string, boolean> | undefined,
  labelToId: Record<string, string | number> | undefined,
  options?: { includeUnmappedLabels?: boolean },
): (string | number)[] =>
  Object.entries(selectedMap || {})
    .filter(([, on]) => !!on)
    .map(([label]) => {
      const mapped = labelToId?.[label]
      if (mapped != null) return mapped
      return options?.includeUnmappedLabels ? label : null
    })
    .filter((value): value is string | number => value !== null)

const trimString = (value: unknown): string => (typeof value === 'string' ? value.trim() : '')

export type FilterFeatureOptions = {
  includeHospitalityGroupsFilter?: boolean
}

const shouldIncludeHospitalityGroupsFilter = (options?: FilterFeatureOptions): boolean => options?.includeHospitalityGroupsFilter ?? true

export function filtersToParams(filters?: FiltersState | null, active_venue_group_id?: number, options?: FilterFeatureOptions): ListSpacesParams {
  if (!filters) return { page: 1, active_venue_group_id }
  const includeHospitalityGroupsFilter = shouldIncludeHospitalityGroupsFilter(options)

  const prices = Object.entries(filters.pricePoints ?? {})
    .filter(([, v]) => v)
    .map(([k]) => PRICE_POINT_SLUG_BY_LEVEL[Number(k)])

  // Highest selected google threshold
  let min_google_rating: number | undefined
  for (const [label, on] of Object.entries(filters.google ?? {})) {
    if (!on) continue
    const num = parseFloat(label)
    if (Number.isFinite(num)) {
      if (typeof min_google_rating === 'undefined' || num < min_google_rating) {
        min_google_rating = num
      }
    }
  }

  const space_types = selectedMapToKeys(filters.spaceTypes ?? {}, LABEL_TO_SPACE_TYPE)
  const privacy = selectedMapToKeys(filters.privacyTypes ?? {}, LABEL_TO_PRIVACY)
  const noise = selectedMapToKeys(filters.noisePrivacy ?? {}, LABEL_TO_NOISE)
  const amenities = selectedMapToKeys(filters.amenities ?? {}, LABEL_TO_AMENITY)
  const av = selectedMapToKeys(filters.av ?? {}, LABEL_TO_AV)
  const outdoor_space_types = selectedMapToKeys(filters.outdoor ?? {}, LABEL_TO_OUTDOOR)
  const windows = selectedMapToKeys(filters.windows ?? {}, LABEL_TO_WINDOWS)
  const michelin_ratings = selectedMapToKeys(filters.michelin ?? {}, LABEL_TO_MICHELIN)
  // Support both legacy shapes:
  // - keys are slugs (e.g. "notable_chef")
  // - keys are labels (e.g. "Notable Chef")
  const ai_filter_tags = Object.entries(filters.venueHighlights || {})
    .filter(([, on]) => !!on)
    .map(([key]) => (VENUE_HIGHLIGHT_SLUG_TO_LABEL[key] ? key : (VENUE_HIGHLIGHT_LABEL_TO_SLUG[key] ?? null)))
    .filter((slug): slug is string => !!slug)

  const trust_signals = Object.entries(filters.editorialRecognition || {})
    .filter(([, on]) => !!on)
    .map(([key]) => (isVenueTrustSignalDomain(key) ? key : null))
    .filter((domain): domain is VenueTrustSignalDomain => domain !== null)

  // Dynamic label -> id mappings for cuisines, venue_types, neighborhoods.
  // Cuisine filters may be restored from URL/map-bounds state before the
  // label->id map is present, so keep the label as a Typesense name fallback.
  const cuisine_values = selectedMappedFilterValues(filters.cuisines, filters.labelToCuisineId, { includeUnmappedLabels: true })
  const venue_type_ids = selectedMappedFilterValues(filters.venueTypes, filters.labelToVenueTypeId)
  const neighborhood_ids = selectedMappedFilterValues(filters.neighborhoods, filters.labelToNeighborhoodId)

  // Venue tag selections — translate selected tag ids into a union of venue ids.
  // If no tag is selected, leave `venue_ids` as undefined (filter inactive).
  // If tags are selected but the lookup map hasn't loaded yet, also undefined.
  // If tags are selected and the union is empty, pass an empty array so the
  // search backend can short-circuit to zero results.
  const selectedTagIds = Object.entries(filters.venueTags || {})
    .filter(([, on]) => !!on)
    .map(([id]) => Number(id))
    .filter((id) => Number.isFinite(id) && id > 0)
  let venue_ids: number[] | undefined
  if (selectedTagIds.length > 0 && filters.venueTagToVenueIds) {
    const set = new Set<number>()
    for (const tagId of selectedTagIds) {
      const ids = filters.venueTagToVenueIds[tagId]
      if (ids) for (const v of ids) set.add(v)
    }
    venue_ids = Array.from(set)
  }

  // Compute bounding box geohashes from selected location + radius (in miles)
  let nw: string | undefined
  let se: string | undefined
  // Prefer explicit bounds from map when provided
  if (filters.boundsNwGeohash && filters.boundsSeGeohash) {
    nw = filters.boundsNwGeohash
    se = filters.boundsSeGeohash
  } else if (typeof filters.locationLat === 'number' && typeof filters.locationLng === 'number') {
    const lat = filters.locationLat
    const lng = filters.locationLng
    const radiusMiles = typeof filters.radiusMiles === 'number' && filters.radiusMiles > 0 ? filters.radiusMiles : DEFAULT_LOCATION_RADIUS_MILES
    const earthRadiusMiles = 3958.8
    const rad = radiusMiles / earthRadiusMiles
    const deg = (r: number) => (r * 180) / Math.PI
    const dLat = deg(rad)
    const dLng = deg(rad) / Math.max(Math.cos((lat * Math.PI) / 180), 0.00001)
    const clampLat = (v: number) => Math.max(-90, Math.min(90, v))
    const normLng = (v: number) => {
      // normalize to [-180, 180]
      let x = v
      while (x < -180) x += 360
      while (x > 180) x -= 360
      return x
    }
    const nwLat = clampLat(lat + dLat)
    const nwLng = normLng(lng - dLng)
    const seLat = clampLat(lat - dLat)
    const seLng = normLng(lng + dLng)
    try {
      nw = Geohash.encode(nwLat, nwLng)
      se = Geohash.encode(seLat, seLng)
    } catch {
      // Ignore invalid geo bounds; the search will run without a location box.
    }
  }

  const params: ListSpacesParams = {
    page: 1,
    venue_name: filters.venueName || undefined,
    google_place_id: filters.googlePlaceId || undefined,
    standing_guest_count: filters.standingGuestCount || undefined,
    seated_guest_count: filters.seatedGuestCount || undefined,
    f_and_b_minimum_from: filters.fbMin || undefined,
    f_and_b_minimum_to: filters.fbMax || undefined,
    space_types: space_types.length ? space_types : undefined,
    privacy: privacy.length ? privacy : undefined,
    noise: noise.length ? noise : undefined,
    amenities: amenities.length ? amenities : undefined,
    av: av.length ? av : undefined,
    outdoor_space_types: outdoor_space_types.length ? outdoor_space_types : undefined,
    windows: windows.length ? windows : undefined,
    prices: prices.length ? prices : undefined,
    michelin_ratings: michelin_ratings.length ? michelin_ratings : undefined,
    ai_filter_tags: ai_filter_tags.length ? ai_filter_tags : undefined,
    trust_signals: trust_signals.length ? trust_signals : undefined,
    venue_management_group_caliber: includeHospitalityGroupsFilter && filters.topHospitalityGroups ? 'high' : undefined,
    min_google_rating: typeof min_google_rating === 'number' ? min_google_rating : undefined,
    cuisines: cuisine_values.length ? cuisine_values : undefined,
    venue_types: venue_type_ids.length ? venue_type_ids : undefined,
    neighborhoods: neighborhood_ids.length ? neighborhood_ids : undefined,
    nw: nw ?? undefined,
    se: se ?? undefined,
    vibe_search: trimString(filters.vibe) || undefined,
    active_venue_group_id,
    venue_ids,
  }

  return params
}

export function countAppliedFilters(filters: FiltersState | null | undefined, options?: FilterFeatureOptions): number {
  if (!filters) return 0
  const includeHospitalityGroupsFilter = shouldIncludeHospitalityGroupsFilter(options)
  const countTrue = (map: Record<string | number, boolean> | undefined): number => (map ? Object.values(map).reduce((sum, v) => sum + (v ? 1 : 0), 0) : 0)
  const isNonEmpty = (s: string | undefined): boolean => !!s && s.trim().length > 0

  let count = 0
  const hasNearLocation = isNonEmpty(filters.location) || (typeof filters.locationLat === 'number' && typeof filters.locationLng === 'number')
  count += hasNearLocation ? 1 : 0
  count += isNonEmpty(filters.vibe) ? 1 : 0
  // Count bounds selection as a single filter if both corners are set
  count += isNonEmpty(filters.boundsNwGeohash) && isNonEmpty(filters.boundsSeGeohash) ? 1 : 0
  count += !hasNearLocation && typeof filters.radiusMiles === 'number' ? 1 : 0
  count += typeof filters.standingGuestCount === 'number' ? 1 : 0
  count += typeof filters.seatedGuestCount === 'number' ? 1 : 0
  count += typeof filters.fbMin === 'number' || typeof filters.fbMax === 'number' ? 1 : 0
  // Count venueName filter - either by name search or by google place_id
  count += isNonEmpty(filters.venueName) || isNonEmpty(filters.googlePlaceId) ? 1 : 0
  count += countTrue(filters.spaceTypes)
  count += countTrue(filters.privacyTypes)
  count += countTrue(filters.noisePrivacy)
  count += countTrue(filters.amenities)
  count += countTrue(filters.av)
  count += countTrue(filters.outdoor)
  count += countTrue(filters.windows)
  count += countTrue(filters.pricePoints)
  count += countTrue(filters.google)
  count += countTrue(filters.michelin)
  count += countTrue(filters.venueTypes)
  count += countTrue(filters.venueHighlights)
  count += countTrue(filters.editorialRecognition)
  count += includeHospitalityGroupsFilter && filters.topHospitalityGroups ? 1 : 0
  count += countTrue(filters.cuisines)
  count += countTrue(filters.neighborhoods)
  count += countTrue(filters.venueTags)
  return count
}

type BooleanFilterField =
  | 'spaceTypes'
  | 'privacyTypes'
  | 'noisePrivacy'
  | 'amenities'
  | 'av'
  | 'outdoor'
  | 'windows'
  | 'pricePoints'
  | 'google'
  | 'michelin'
  | 'venueTypes'
  | 'venueHighlights'
  | 'editorialRecognition'
  | 'cuisines'
  | 'neighborhoods'
  | 'venueTags'

export type AppliedFilterRemoval =
  | { type: 'near' }
  | { type: 'mapBounds' }
  | { type: 'venueName' }
  | { type: 'vibe' }
  | { type: 'guestCount'; field: 'standingGuestCount' | 'seatedGuestCount' }
  | { type: 'fAndB' }
  | { type: 'booleanValue'; field: BooleanFilterField; value: string }
  | { type: 'booleanCategory'; field: BooleanFilterField }
  | { type: 'combinedVenueInsightValue'; field: 'venueHighlights' | 'editorialRecognition'; value: string }
  | { type: 'combinedVenueInsights' }
  | { type: 'topHospitalityGroups' }

export type AppliedFilterChipValue = {
  id: string
  category: string
  label: string
  tooltip?: string
  priority: boolean
  icon?: AppliedFilterIcon
  removal: AppliedFilterRemoval
}

export type AppliedFilterIcon = 'map-pin' | 'google' | 'michelin-star'

export type AppliedFilterChip = AppliedFilterChipValue & (
  | { kind: 'value' }
  | { kind: 'rollup'; children: AppliedFilterChipValue[] }
)

type SelectedFilterValue = {
  value: string
  label: string
}

const selectedValuesFromRecord = (
  record: Record<string, boolean | undefined> | Record<number, boolean | undefined> | undefined,
  labelFor: (value: string) => string = (value) => value,
): SelectedFilterValue[] =>
  Object.entries(record ?? {})
    .filter(([, checked]) => !!checked)
    .map(([value]) => ({ value, label: labelFor(value) }))

const formatMiles = (miles: number): string => `${miles} mi`

const formatMoney = (value: number): string => `$${value.toLocaleString('en-US')}`

const shortenLabel = (value: string, maxLength: number): string => {
  const trimmed = value.trim()
  const primary = trimmed.split(',')[0]?.trim() || trimmed
  if (primary.length <= maxLength) return primary
  return `${primary.slice(0, Math.max(0, maxLength - 3)).trimEnd()}...`
}

const labelVenueHighlight = (value: string): string => VENUE_HIGHLIGHT_SLUG_TO_LABEL[value] ?? value

const labelEditorialRecognition = (value: string): string =>
  isVenueTrustSignalDomain(value) ? VENUE_TRUST_SIGNAL_LABELS[value] : value

const labelPricePoint = (value: string): string => {
  const level = Number(value)
  if (!Number.isFinite(level) || level < 1) return value
  return '$'.repeat(Math.min(level, 4))
}

const labelGoogleRating = (value: string): string => `${value}+`

const labelMichelin = (value: string): string => {
  if (value === '3 Stars') return '3-star'
  if (value === '2 Stars') return '2-star'
  if (value === '1 Star') return '1-star'
  return value
}

const labelVenueTag = (filters: FiltersState, value: string): string => {
  const id = Number(value)
  if (!Number.isFinite(id)) return value
  return filters.venueTagLabelById?.[id] ?? `Tag ${value}`
}

const createValueChip = (
  category: string,
  value: SelectedFilterValue,
  removal: AppliedFilterRemoval,
  options: { priority?: boolean; tooltip?: string; icon?: AppliedFilterIcon; idPrefix?: string } = {},
): AppliedFilterChip => ({
  id: `${options.idPrefix ?? category}:${value.value}`,
  kind: 'value',
  category,
  label: value.label,
  tooltip: options.tooltip,
  priority: options.priority ?? false,
  icon: options.icon,
  removal,
})

const appendBooleanCategory = (
  chips: AppliedFilterChip[],
  category: string,
  field: BooleanFilterField,
  values: SelectedFilterValue[],
  options: { priority?: boolean; icon?: AppliedFilterIcon; rollupLabel?: (values: SelectedFilterValue[]) => string } = {},
) => {
  if (values.length === 0) return

  const children: AppliedFilterChipValue[] = values.map((value) => ({
    id: `${field}:${value.value}`,
    category,
    label: value.label,
    priority: options.priority ?? false,
    icon: options.icon,
    removal: { type: 'booleanValue', field, value: value.value },
  }))

  if (values.length === 1) {
    chips.push(...children.map((child) => ({ ...child, kind: 'value' as const })))
    return
  }

  chips.push({
    id: `${field}:rollup`,
    kind: 'rollup',
    category,
    label: options.rollupLabel?.(values) ?? `${category} (${values.length})`,
    priority: options.priority ?? false,
    icon: options.icon,
    removal: { type: 'booleanCategory', field },
    children,
  })
}

const formatPricePointSummary = (values: SelectedFilterValue[]): string => {
  const levels = values
    .map((value) => Number(value.value))
    .filter((level) => Number.isFinite(level) && level >= 1 && level <= 4)
    .sort((a, b) => a - b)

  if (levels.length === 0) return 'Price Point'

  const min = levels[0]
  const coversEveryHigherLevel = typeof min === 'number' && levels.every((level, index) => level === min + index) && levels.at(-1) === 4
  if (typeof min === 'number' && coversEveryHigherLevel) return `${'$'.repeat(min)}+`

  return levels.map((level) => '$'.repeat(level)).join(',')
}

const appendGoogleRatingCategory = (chips: AppliedFilterChip[], filters: FiltersState) => {
  const selectedRatings = selectedValuesFromRecord(filters.google)
    .map((value) => Number(value.value))
    .filter((rating) => Number.isFinite(rating))
    .sort((a, b) => a - b)

  const threshold = selectedRatings[0]
  if (typeof threshold !== 'number') return

  chips.push(createValueChip(
    'Google Rating',
    { value: String(threshold), label: labelGoogleRating(String(threshold)) },
    { type: 'booleanCategory', field: 'google' },
    { icon: 'google' },
  ))
}

const appendMichelinCategory = (chips: AppliedFilterChip[], filters: FiltersState) => {
  appendBooleanCategory(
    chips,
    'Michelin',
    'michelin',
    selectedValuesFromRecord(filters.michelin, labelMichelin),
    {
      icon: 'michelin-star',
      rollupLabel: (values) => values.map((value) => value.label).join(', '),
    },
  )
}

const appendCombinedVenueInsightsCategory = (
  chips: AppliedFilterChip[],
  filters: FiltersState,
) => {
  const values: Array<SelectedFilterValue & { field: 'venueHighlights' | 'editorialRecognition' }> = [
    ...selectedValuesFromRecord(filters.venueHighlights, labelVenueHighlight).map((value) => ({ ...value, field: 'venueHighlights' as const })),
    ...selectedValuesFromRecord(filters.editorialRecognition, labelEditorialRecognition).map((value) => ({ ...value, field: 'editorialRecognition' as const })),
  ]

  if (values.length === 0) return

  const children: AppliedFilterChipValue[] = values.map((value) => ({
    id: `${value.field}:${value.value}`,
    category: 'Venue Insights',
    label: value.label,
    priority: false,
    removal: { type: 'combinedVenueInsightValue', field: value.field, value: value.value },
  }))

  if (values.length === 1) {
    chips.push({ ...children[0], kind: 'value' as const })
    return
  }

  chips.push({
    id: 'venueInsights:rollup',
    kind: 'rollup',
    category: 'Venue Insights',
    label: `Venue Insights (${values.length})`,
    priority: false,
    removal: { type: 'combinedVenueInsights' },
    children,
  })
}

const getNearChipLabel = (filters: FiltersState): string => {
  const placeLabel = trimString(filters.locationPlaceLabel) || trimString(filters.location)
  const shortPlaceLabel = placeLabel ? shortenLabel(placeLabel, 22) : ''
  if (shortPlaceLabel && typeof filters.radiusMiles === 'number') return `${shortPlaceLabel} · ${formatMiles(filters.radiusMiles)}`
  if (shortPlaceLabel) return shortPlaceLabel
  if (typeof filters.radiusMiles === 'number') return `Near: ${formatMiles(filters.radiusMiles)}`
  return 'Near'
}

const hasNearFilter = (filters: FiltersState): boolean =>
  trimString(filters.location).length > 0 ||
  typeof filters.locationLat === 'number' ||
  typeof filters.locationLng === 'number' ||
  typeof filters.radiusMiles === 'number'

export function buildAppliedFilterChips(filters: FiltersState | null | undefined, options?: FilterFeatureOptions): AppliedFilterChip[] {
  if (!filters) return []
  const includeHospitalityGroupsFilter = shouldIncludeHospitalityGroupsFilter(options)
  const chips: AppliedFilterChip[] = []

  if (hasNearFilter(filters)) {
    chips.push(createValueChip(
      'Near',
      { value: 'near', label: getNearChipLabel(filters) },
      { type: 'near' },
      { priority: true, tooltip: filters.location || undefined, icon: 'map-pin', idPrefix: 'near' },
    ))
  }

  if (typeof filters.standingGuestCount === 'number') {
    chips.push(createValueChip(
      'Guest Count',
      { value: 'standing', label: `Standing: ${filters.standingGuestCount}` },
      { type: 'guestCount', field: 'standingGuestCount' },
      { priority: true },
    ))
  }

  if (typeof filters.seatedGuestCount === 'number') {
    chips.push(createValueChip(
      'Guest Count',
      { value: 'seated', label: `Seated: ${filters.seatedGuestCount}` },
      { type: 'guestCount', field: 'seatedGuestCount' },
      { priority: true },
    ))
  }

  appendBooleanCategory(chips, 'Privacy Type', 'privacyTypes', selectedValuesFromRecord(filters.privacyTypes), { priority: true })

  if (filters.boundsNwGeohash && filters.boundsSeGeohash) {
    chips.push(createValueChip('Map area', { value: 'bounds', label: 'Map area' }, { type: 'mapBounds' }))
  }

  const venueName = trimString(filters.venueName)
  if (venueName.length > 0 || filters.googlePlaceId) {
    const venueNameLabel = venueName || 'Selected venue'
    chips.push(createValueChip(
      'Venue Name',
      { value: 'venue-name', label: `Name: ${shortenLabel(venueNameLabel, 32)}` },
      { type: 'venueName' },
      { tooltip: venueNameLabel },
    ))
  }

  const vibe = trimString(filters.vibe)
  if (vibe.length > 0) {
    chips.push(createValueChip(
      'Vibe',
      { value: 'vibe', label: `Vibe: ${shortenLabel(vibe, 32)}` },
      { type: 'vibe' },
      { tooltip: vibe },
    ))
  }

  if (typeof filters.fbMin === 'number' || typeof filters.fbMax === 'number') {
    const label = typeof filters.fbMin === 'number' && typeof filters.fbMax === 'number'
      ? `F&B: ${formatMoney(filters.fbMin)}-${formatMoney(filters.fbMax)}`
      : typeof filters.fbMin === 'number'
        ? `F&B: ${formatMoney(filters.fbMin)}+`
        : `F&B: up to ${formatMoney(filters.fbMax ?? 0)}`
    chips.push(createValueChip('F&B Minimum', { value: 'f-and-b', label }, { type: 'fAndB' }))
  }

  appendBooleanCategory(chips, 'Space Type', 'spaceTypes', selectedValuesFromRecord(filters.spaceTypes))
  appendBooleanCategory(chips, 'Noise', 'noisePrivacy', selectedValuesFromRecord(filters.noisePrivacy))
  appendBooleanCategory(chips, 'Amenities', 'amenities', selectedValuesFromRecord(filters.amenities))
  appendBooleanCategory(chips, 'A/V', 'av', selectedValuesFromRecord(filters.av))
  appendBooleanCategory(chips, 'Outdoor', 'outdoor', selectedValuesFromRecord(filters.outdoor))
  appendBooleanCategory(chips, 'Windows', 'windows', selectedValuesFromRecord(filters.windows))
  appendBooleanCategory(chips, 'Price Point', 'pricePoints', selectedValuesFromRecord(filters.pricePoints, labelPricePoint), { rollupLabel: formatPricePointSummary })
  appendGoogleRatingCategory(chips, filters)
  appendMichelinCategory(chips, filters)
  appendBooleanCategory(chips, 'Venue Type', 'venueTypes', selectedValuesFromRecord(filters.venueTypes))
  appendCombinedVenueInsightsCategory(chips, filters)
  if (includeHospitalityGroupsFilter && filters.topHospitalityGroups) {
    chips.push(createValueChip(
      'Venue Insights',
      { value: 'top-hospitality-groups', label: 'Notable Hospitality Group' },
      { type: 'topHospitalityGroups' },
    ))
  }
  appendBooleanCategory(chips, 'Cuisine', 'cuisines', selectedValuesFromRecord(filters.cuisines))
  appendBooleanCategory(chips, 'Neighborhood', 'neighborhoods', selectedValuesFromRecord(filters.neighborhoods))
  appendBooleanCategory(chips, 'Tags', 'venueTags', selectedValuesFromRecord(filters.venueTags, (value) => labelVenueTag(filters, value)))

  return chips
}

const clearStringRecord = (record: Record<string, boolean> | undefined): Record<string, boolean> =>
  Object.fromEntries(Object.keys(record ?? {}).map((key) => [key, false]))

const clearNumberRecord = (record: Record<number, boolean> | undefined): Record<number, boolean> =>
  Object.fromEntries(Object.keys(record ?? {}).map((key) => [Number(key), false]))

const clearEditorialRecord = (
  record: Partial<Record<VenueTrustSignalDomain, boolean>> | undefined,
): Partial<Record<VenueTrustSignalDomain, boolean>> =>
  Object.fromEntries(Object.keys(record ?? {}).map((key) => [key, false]))

const setBooleanValue = (filters: FiltersState, field: BooleanFilterField, value: string, checked: boolean): FiltersState => {
  switch (field) {
    case 'spaceTypes':
      return { ...filters, spaceTypes: { ...filters.spaceTypes, [value]: checked } }
    case 'privacyTypes':
      return { ...filters, privacyTypes: { ...filters.privacyTypes, [value]: checked } }
    case 'noisePrivacy':
      return { ...filters, noisePrivacy: { ...filters.noisePrivacy, [value]: checked } }
    case 'amenities':
      return { ...filters, amenities: { ...filters.amenities, [value]: checked } }
    case 'av':
      return { ...filters, av: { ...filters.av, [value]: checked } }
    case 'outdoor':
      return { ...filters, outdoor: { ...filters.outdoor, [value]: checked } }
    case 'windows':
      return { ...filters, windows: { ...filters.windows, [value]: checked } }
    case 'pricePoints':
      return { ...filters, pricePoints: { ...filters.pricePoints, [Number(value)]: checked } }
    case 'google':
      return { ...filters, google: { ...filters.google, [value]: checked } }
    case 'michelin':
      return { ...filters, michelin: { ...filters.michelin, [value]: checked } }
    case 'venueTypes':
      return { ...filters, venueTypes: { ...filters.venueTypes, [value]: checked } }
    case 'venueHighlights':
      return { ...filters, venueHighlights: { ...filters.venueHighlights, [value]: checked } }
    case 'editorialRecognition':
      return { ...filters, editorialRecognition: { ...(filters.editorialRecognition ?? {}), [value]: checked } }
    case 'cuisines':
      return { ...filters, cuisines: { ...filters.cuisines, [value]: checked } }
    case 'neighborhoods':
      return { ...filters, neighborhoods: { ...filters.neighborhoods, [value]: checked } }
    case 'venueTags':
      return { ...filters, venueTags: { ...(filters.venueTags ?? {}), [Number(value)]: checked } }
  }
}

const clearBooleanCategory = (filters: FiltersState, field: BooleanFilterField): FiltersState => {
  switch (field) {
    case 'spaceTypes':
      return { ...filters, spaceTypes: clearStringRecord(filters.spaceTypes) }
    case 'privacyTypes':
      return { ...filters, privacyTypes: clearStringRecord(filters.privacyTypes) }
    case 'noisePrivacy':
      return { ...filters, noisePrivacy: clearStringRecord(filters.noisePrivacy) }
    case 'amenities':
      return { ...filters, amenities: clearStringRecord(filters.amenities) }
    case 'av':
      return { ...filters, av: clearStringRecord(filters.av) }
    case 'outdoor':
      return { ...filters, outdoor: clearStringRecord(filters.outdoor) }
    case 'windows':
      return { ...filters, windows: clearStringRecord(filters.windows) }
    case 'pricePoints':
      return { ...filters, pricePoints: clearNumberRecord(filters.pricePoints) }
    case 'google':
      return { ...filters, google: clearStringRecord(filters.google) }
    case 'michelin':
      return { ...filters, michelin: clearStringRecord(filters.michelin) }
    case 'venueTypes':
      return { ...filters, venueTypes: clearStringRecord(filters.venueTypes) }
    case 'venueHighlights':
      return { ...filters, venueHighlights: clearStringRecord(filters.venueHighlights) }
    case 'editorialRecognition':
      return { ...filters, editorialRecognition: clearEditorialRecord(filters.editorialRecognition) }
    case 'cuisines':
      return { ...filters, cuisines: clearStringRecord(filters.cuisines) }
    case 'neighborhoods':
      return { ...filters, neighborhoods: clearStringRecord(filters.neighborhoods) }
    case 'venueTags':
      return { ...filters, venueTags: clearNumberRecord(filters.venueTags) }
  }
}

const normalizeAfterRemoval = (filters: FiltersState): FiltersState | null =>
  countAppliedFilters(filters) === 0 ? null : filters

export function applyAppliedFilterRemoval(filters: FiltersState, removal: AppliedFilterRemoval): FiltersState | null {
  switch (removal.type) {
    case 'near':
      return normalizeAfterRemoval({
        ...filters,
        location: '',
        locationPlaceLabel: undefined,
        locationLat: undefined,
        locationLng: undefined,
        radiusMiles: undefined,
        boundsNwGeohash: undefined,
        boundsSeGeohash: undefined,
      })
    case 'mapBounds':
      return normalizeAfterRemoval({ ...filters, boundsNwGeohash: undefined, boundsSeGeohash: undefined })
    case 'venueName':
      return normalizeAfterRemoval({ ...filters, venueName: '', googlePlaceId: undefined })
    case 'vibe':
      return normalizeAfterRemoval({ ...filters, vibe: '' })
    case 'guestCount':
      return normalizeAfterRemoval({ ...filters, [removal.field]: undefined })
    case 'fAndB':
      return normalizeAfterRemoval({ ...filters, fbMin: undefined, fbMax: undefined })
    case 'booleanValue':
      return normalizeAfterRemoval(setBooleanValue(filters, removal.field, removal.value, false))
    case 'booleanCategory':
      return normalizeAfterRemoval(clearBooleanCategory(filters, removal.field))
    case 'combinedVenueInsightValue':
      return normalizeAfterRemoval(setBooleanValue(filters, removal.field, removal.value, false))
    case 'combinedVenueInsights':
      return normalizeAfterRemoval(clearBooleanCategory(clearBooleanCategory(filters, 'venueHighlights'), 'editorialRecognition'))
    case 'topHospitalityGroups':
      return normalizeAfterRemoval({ ...filters, topHospitalityGroups: false })
  }
}
